<template>
  <!-- DP-4478: the order's Shipping box — how many items have shipped, how many
       are still waiting, how many labels exist, and the way into Manage
       Shipping (Figma frames 1573 and 1610). Replaces the single
       "Created / Not Created" row, which could only describe one label.
       `order-item` + `rd-deep-1` are the wrapper and white card the item and
       coupon rows use, so the box lines up with them and keeps the right-hand
       gutter their selection circles sit in. -->
  <div class="d-flex order-item">
    <div class="flex-grow-1 rd-deep-1 dp-ship-box">
      <div v-if="!overview && loading" class="dp-ship-muted">
        <b-spinner small /><span class="ml-2">Loading shipping…</span>
      </div>
      <div v-else-if="!overview && failed" class="dp-ship-muted">
        The shipping details could not be loaded.
        <a href="#" @click.prevent="load">Try again</a>
      </div>
      <template v-else-if="overview">
        <div class="dp-ship-text">
          <div class="dp-ship-title">{{ title }}</div>
          <div class="dp-ship-sub" v-if="carriers">{{ carriers }}</div>
        </div>
        <span class="dp-ship-pill" :class="`dp-ship-pill--${badge.variant}`">{{ badge.text }}</span>
        <b-button class="dp-ship-manage" variant="outline-light" @click="openManageShipping">
          <PackageIcon class="mr-2" />Manage Shipping
        </b-button>
      </template>
    </div>
  </div>
</template>

<script>
import { mapMutations } from 'vuex';
import apiOrders from '@/api/orders';
import PackageIcon from '@/views/modals/ManageShipping/PackageIcon.vue';
import { shippingBadge, shippingTitle } from '@/views/modals/ManageShipping/shipping-format';

export default {
  name: 'OrderItemShipmentDetail',
  components: {
    PackageIcon,
  },
  props: {
    order: Object,
  },
  data() {
    return {
      overview: null,
      loading: false,
      failed: false,
    };
  },
  computed: {
    summary() {
      return this._.get(this.overview, 'summary', {});
    },
    title() {
      return shippingTitle(this.summary);
    },
    badge() {
      return shippingBadge(this.summary);
    },
    carriers() {
      return (this.summary.carriers || []).join(', ');
    },
  },
  watch: {
    'order.id': {
      handler() {
        this.overview = null;
        this.load();
      },
      immediate: true,
    },
  },
  created() {
    // A label created for this order changes what the box should say.
    this.$root.$on('shipmentsChanged', this.onShipmentsChanged);
  },
  beforeDestroy() {
    this.$root.$off('shipmentsChanged', this.onShipmentsChanged);
  },
  methods: {
    ...mapMutations({
      setSelectedOrderId: 'SET_SELECTED_ORDER_ID',
    }),
    onShipmentsChanged(orderId, overview) {
      if (orderId !== this._.get(this.order, 'id')) return;
      if (overview) {
        this.overview = overview;
      } else {
        this.load();
      }
    },
    async load() {
      const orderId = this._.get(this.order, 'id');
      if (!orderId) return;

      this.loading = true;
      this.failed = false;
      try {
        const response = await apiOrders.getOrderShipments(orderId);
        // The row may have moved on to another order while this was loading.
        if (orderId === this._.get(this.order, 'id')) {
          this.overview = response.data;
        }
      } catch (error) {
        this.failed = true;
      } finally {
        this.loading = false;
      }
    },
    openManageShipping() {
      this.setSelectedOrderId(this.order.id);
      this.$bvModal.show('manage-shipping-modal');
    },
  },
};
</script>

<style scoped>
.dp-ship-box {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
}
.dp-ship-text {
  flex: 1 1 auto;
  min-width: 0;
}
.dp-ship-title {
  font-size: 14px;
  font-weight: 700;
  color: #292a2d;
}
.dp-ship-sub {
  font-size: 12px;
  color: #718098;
  margin-top: 2px;
}
.dp-ship-muted {
  font-size: 13px;
  color: #718098;
}
.dp-ship-pill {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  padding: 8px 10px;
  border-radius: 100px;
  white-space: nowrap;
}
.dp-ship-pill--shipped {
  background: #5ac072;
  color: #fff;
}
.dp-ship-pill--pending {
  background: #edf0f4;
  color: #718098;
}
.dp-ship-manage {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  font-size: 14px;
  font-weight: 500;
  color: #292a2d;
  border: 1px solid #b1bfd5;
  border-radius: 6px;
  background: #fff;
}
@media (max-width: 720px) {
  .dp-ship-box {
    flex-wrap: wrap;
  }
  .dp-ship-text {
    flex-basis: 100%;
  }
}
</style>
